import { faCalendar, faClose } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import InputField from "../../components/InputField/InputField"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"

interface TimeClockPeriodFilterPopupProps {
  initialStartDate: string
  initialEndDate: string
  onApply: (startDate: string, endDate: string) => void
  onClose: () => void
}

export const TimeClockPeriodFilterPopup: React.FC<TimeClockPeriodFilterPopupProps> = ({
  initialStartDate,
  initialEndDate,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  const [startDate, setStartDate] = useState(initialStartDate)

  const [endDate, setEndDate] = useState(initialEndDate)

  const handleApply = () => {
    onApply(startDate, endDate)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("timeClock:my.period_filter_title")}
              icon={faCalendar}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-period-filter"
                    title={t("helpPopup:global.timeClockPeriodFilter.title")}
                    description={t("helpPopup:global.timeClockPeriodFilter.description")}
                    bullets={t("helpPopup:global.timeClockPeriodFilter.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockPeriodFilterPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="flex flex-col gap-3 p-2"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleApply()
              }}
            >
              <InputField
                type="date"
                value={startDate}
                icon={faCalendar}
                placeholder={t("timeClock:management.start_date")}
                onChange={(event) => setStartDate(event.target.value)}
                descriptionKey="interactionDescriptions:pages.timeClock.timeClockPeriodFilterPopup.input.startDate"
              />

              <InputField
                type="date"
                value={endDate}
                icon={faCalendar}
                placeholder={t("timeClock:management.end_date")}
                onChange={(event) => setEndDate(event.target.value)}
                descriptionKey="interactionDescriptions:pages.timeClock.timeClockPeriodFilterPopup.input.endDate"
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  typeButton="submit"
                  label={t("timeClock:my.apply_period_filter")}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  iconColor={THEME.colors.iconBGSecondaryColor}
                  descriptionKey="interactionDescriptions:pages.timeClock.timeClockPeriodFilterPopup.button.apply"
                />
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
